// src/components/user/ChangePasswordForm.jsx
// Signed-in password change (Settings › Security). Acts on the session's own
// account — POST /api/auth/change-password reads the user from the auth
// cookie, never from anything sent here. For staff users the server also
// signs out every other session and re-issues this one's cookie.
import React, { useState } from 'react';
import PasswordInput from '../shared/PasswordInput';
import { postJson } from '../../services/http';

export const MIN_PASSWORD_LENGTH = 8; // matches the server + UserAdd/OwnerAdd/ResetPassword

const EMPTY = { currentPassword: '', newPassword: '', confirmPassword: '' };

export function ChangePasswordForm({ onDone }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { ok: boolean, text: string }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setResult(null);
    if (form.newPassword.length < MIN_PASSWORD_LENGTH) {
      return setResult({ ok: false, text: `New password must be at least ${MIN_PASSWORD_LENGTH} characters.` });
    }
    if (form.newPassword !== form.confirmPassword) {
      return setResult({ ok: false, text: 'New passwords do not match.' });
    }
    if (form.newPassword === form.currentPassword) {
      return setResult({ ok: false, text: 'New password must be different from the current one.' });
    }

    setBusy(true);
    try {
      const data = await postJson('/api/auth/change-password', {
        currentPassword: form.currentPassword,
        newPassword: form.newPassword,
      });
      setForm(EMPTY);
      setResult({
        ok: true,
        text: data.otherSessionsSignedOut
          ? 'Password updated. Any other devices signed in to your account have been signed out.'
          : 'Password updated.',
      });
      onDone?.();
    } catch (err) {
      setResult({ ok: false, text: err.response?.data?.message || err.message || 'Failed to update password.' });
    } finally {
      setBusy(false);
    }
  };

  const field = (name, label, autoComplete) => (
    <div className="form-group" style={{ marginBottom: 12 }}>
      <label htmlFor={`cpw-${name}`} style={{ display: 'block', fontWeight: 600, marginBottom: 4 }}>{label}</label>
      <PasswordInput
        id={`cpw-${name}`}
        name={name}
        value={form[name]}
        onChange={handleChange}
        autoComplete={autoComplete}
        required
        disabled={busy}
        className="form-control"
      />
    </div>
  );

  return (
    <form onSubmit={handleSubmit} style={{ maxWidth: 420 }} aria-label="Change password">
      {field('currentPassword', 'Current password', 'current-password')}
      {field('newPassword', 'New password', 'new-password')}
      {field('confirmPassword', 'Confirm new password', 'new-password')}
      <p style={{ fontSize: '0.9rem', margin: '0 0 12px' }} className="note info note-block">
        At least {MIN_PASSWORD_LENGTH} characters.
      </p>
      <button type="submit" className="btn btn-primary" disabled={busy}>
        {busy ? 'Updating…' : 'Update password'}
      </button>
      {result && (
        <div
          className={`note ${result.ok ? 'info' : 'error'} note-block`}
          role={result.ok ? 'status' : 'alert'}
          style={{ marginTop: 12 }}
        >
          {result.text}
        </div>
      )}
    </form>
  );
}

export default ChangePasswordForm;
